/**
 * Contact Details Collector
 *
 * International quick-checkout sheet:
 * - Email required; phone may be left blank (country required only if phone is filled)
 * - Country/phone: all countries via libphonenumber-js + per-country validation
 * - E.164 output for checkout / APIs when phone is provided
 */

(function() {
  'use strict';
  // Customer-facing copy comes from window.theme.strings (emitted by
  // snippets/head.liquid from the locale files) so it follows the storefront
  // language. The English argument stays as the fallback for any context where
  // theme.strings has not loaded.
  function themeStr(key, fallback) {
    var s = window.theme && window.theme.strings;
    return (s && s[key]) || fallback;
  }

  // Display name for a country/emirate, via the translated places map that
  // head.liquid emits. Values submitted to the form NEVER go through this -
  // Stripe and the order record read English; only what the shopper sees.
  function placeName(name) {
    var s = window.theme && window.theme.strings;
    var places = s && s.places;
    return (places && places[name]) || name;
  }


  /** Max national number length (ITU); precise validation on blur/submit */
  var PHONE_NATIONAL_MAX = 20;

  // Default country (ISO + dial) by storefront market
  var COUNTRY_CODES = {
    'en-ae': { code: '+971', country: 'AE', name: 'UAE' },
    'ar-ae': { code: '+971', country: 'AE', name: 'UAE' },
    'en-sa': { code: '+966', country: 'SA', name: 'Saudi Arabia' },
    'ar-sa': { code: '+966', country: 'SA', name: 'Saudi Arabia' },
    'en-gb': { code: '+44', country: 'GB', name: 'UK' },
    'en-sg': { code: '+65', country: 'SG', name: 'Singapore' },
    'en-us': { code: '+1', country: 'US', name: 'USA' },
    'en-eu': { code: '+33', country: 'FR', name: 'France' },
    'en-ca': { code: '+1', country: 'CA', name: 'Canada' },
    'en-au': { code: '+61', country: 'AU', name: 'Australia' },
    'default': { code: '+91', country: 'IN', name: 'India' }
  };

  // Used only when libphonenumber fails to load
  var PHONE_PATTERNS_FALLBACK = {
    AE: /^[0-9]{9}$/,
    SA: /^[0-9]{9}$/,
    GB: /^[0-9]{10,11}$/,
    SG: /^[0-9]{8}$/,
    US: /^[0-9]{10}$/,
    FR: /^[0-9]{9}$/,
    IN: /^[0-9]{10}$/,
    DE: /^[0-9]{10,11}$/,
    IT: /^[0-9]{9,10}$/,
    ES: /^[0-9]{9}$/,
    NL: /^[0-9]{9,10}$/,
    CA: /^[0-9]{10}$/,
    AU: /^[0-9]{9}$/
  };

  var FALLBACK_COUNTRY_ROWS = [
    { code: '+971', country: 'AE', name: 'United Arab Emirates' },
    { code: '+966', country: 'SA', name: 'Saudi Arabia' },
    { code: '+44', country: 'GB', name: 'United Kingdom' },
    { code: '+65', country: 'SG', name: 'Singapore' },
    { code: '+1', country: 'US', name: 'United States' },
    { code: '+33', country: 'FR', name: 'France' },
    { code: '+49', country: 'DE', name: 'Germany' },
    { code: '+39', country: 'IT', name: 'Italy' },
    { code: '+34', country: 'ES', name: 'Spain' },
    { code: '+31', country: 'NL', name: 'Netherlands' },
    { code: '+91', country: 'IN', name: 'India' },
    { code: '+1', country: 'CA', name: 'Canada' },
    { code: '+61', country: 'AU', name: 'Australia' }
  ];

  var cachedCountryRows = null;

  function getPhoneLib() {
    return typeof window !== 'undefined' ? window.libphonenumber : null;
  }

  function getCountryDisplayName(iso) {
    if (!iso) return '';
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        var dn = new Intl.DisplayNames(['en'], { type: 'region' });
        return dn.of(iso) || iso;
      }
    } catch (e) { /* ignore */ }
    return iso;
  }

  function buildOptionValue(iso, dial) {
    return iso + '|' + dial;
  }

  function parseOptionValue(value) {
    if (!value || typeof value !== 'string') return null;
    var i = value.indexOf('|');
    if (i === -1) return null;
    return {
      country: value.slice(0, i),
      code: value.slice(i + 1)
    };
  }

  function getAllCountryRows() {
    if (cachedCountryRows) return cachedCountryRows;

    var lib = getPhoneLib();
    if (!lib || typeof lib.getCountries !== 'function' || typeof lib.getCountryCallingCode !== 'function') {
      cachedCountryRows = FALLBACK_COUNTRY_ROWS.slice();
      return cachedCountryRows;
    }

    var displayNames = null;
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
      }
    } catch (e) {
      displayNames = null;
    }

    var isos = lib.getCountries();
    var rows = isos.map(function(iso) {
      var dial = '+' + String(lib.getCountryCallingCode(iso));
      var label = iso;
      if (displayNames) {
        try {
          label = displayNames.of(iso) || iso;
        } catch (e2) {
          label = iso;
        }
      }
      return {
        code: dial,
        country: iso,
        name: label,
        sortKey: String(label).toLowerCase()
      };
    });

    rows.sort(function(a, b) {
      return a.sortKey.localeCompare(b.sortKey);
    });

    cachedCountryRows = rows;
    return cachedCountryRows;
  }

  function detectMarket() {
    if (window.MarketConfig && typeof window.MarketConfig.getCurrentMarket === 'function') {
      var marketInfo = window.MarketConfig.getCurrentMarket();
      if (marketInfo && marketInfo.key) return marketInfo.key;
    }

    var path = window.location.pathname.toLowerCase();
    if (path.indexOf('/ar-ae/') !== -1) return 'ar-ae';
    if (path.indexOf('/en-ae/') !== -1) return 'en-ae';
    if (path.indexOf('/ar-sa/') !== -1) return 'ar-sa';
    if (path.indexOf('/en-sa/') !== -1) return 'en-sa';
    if (path.indexOf('/en-gb/') !== -1) return 'en-gb';
    if (path.indexOf('/en-sg/') !== -1) return 'en-sg';
    if (path.indexOf('/en-us/') !== -1) return 'en-us';
    if (path.indexOf('/en-eu/') !== -1) return 'en-eu';
    if (path.indexOf('/en-ca/') !== -1) return 'en-ca';
    if (path.indexOf('/en-au/') !== -1) return 'en-au';

    return 'default';
  }

  function getDefaultCountryCode() {
    var market = detectMarket();
    return COUNTRY_CODES[market] || COUNTRY_CODES['default'];
  }

  function findRowByIso(iso) {
    var rows = getAllCountryRows();
    for (var r = 0; r < rows.length; r++) {
      if (rows[r].country === iso) return rows[r];
    }
    return null;
  }

  /** Collapsed UI shows dial only; options still use full "Country (+code)" labels in the native picker */
  function updateDialCodeDisplay() {
    var dropdown = document.getElementById('contact-country-code');
    var span = document.getElementById('contact-country-dial-display');
    if (!dropdown || !span) return;

    var opt = dropdown.options[dropdown.selectedIndex];
    var dial = opt && opt.getAttribute('data-dial') ? opt.getAttribute('data-dial') : '';
    if (!dial) {
      var sel = getSelectedCountryCode();
      dial = sel && sel.code ? sel.code : '+';
    }
    span.textContent = dial;

    var wrap = dropdown.closest('.contact-details__country-select-wrap');
    if (wrap && dial) {
      var extra = Math.max(0, dial.length - 4);
      wrap.style.width = extra ? 'calc(5.25rem + ' + extra * 0.35 + 'rem)' : '';
    }
  }

  function initCountryCodeDropdown() {
    var dropdown = document.getElementById('contact-country-code');
    if (!dropdown) return;

    var defaultCountry = getDefaultCountryCode();
    var rows = getAllCountryRows();

    dropdown.innerHTML = '';

    rows.forEach(function(row) {
      var option = document.createElement('option');
      option.value = buildOptionValue(row.country, row.code);
      option.textContent = placeName(row.name) + ' (' + row.code + ')';
      option.setAttribute('data-country', row.country);
      option.setAttribute('data-dial', row.code);

      if (row.country === defaultCountry.country) {
        option.selected = true;
      }

      dropdown.appendChild(option);
    });

    dropdown.dataset.selectedCountry = defaultCountry.country;
    updateDialCodeDisplay();
  }

  function getSelectedCountryCode() {
    var dropdown = document.getElementById('contact-country-code');
    if (!dropdown || dropdown.selectedIndex < 0) return null;

    var parsed = parseOptionValue(dropdown.value);
    if (parsed) return parsed;

    // Legacy option value (dial only)
    var opt = dropdown.options[dropdown.selectedIndex];
    if (opt && opt.getAttribute('data-country')) {
      return {
        country: opt.getAttribute('data-country'),
        code: opt.getAttribute('data-dial') || dropdown.value
      };
    }

    return null;
  }

  function validateEmail(email) {
    if (!email || email.trim() === '') {
      return { valid: false, message: themeStr('emailRequired', 'Email is required') };
    }

    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return { valid: false, message: themeStr('invalidEmail', 'Please enter a valid email address') };
    }

    return { valid: true };
  }

  function validatePhone(phone, selected) {
    if (!phone || phone.trim() === '') {
      return { valid: true };
    }

    if (!selected || !selected.country) {
      return { valid: false, message: themeStr('selectCountry', 'Please select a country') };
    }

    var lib = getPhoneLib();
    var countryLabel = getCountryDisplayName(selected.country);

    if (lib && typeof lib.parsePhoneNumber === 'function') {
      try {
        var pn = lib.parsePhoneNumber(phone, selected.country);
        if (pn && typeof pn.isValid === 'function' && pn.isValid()) {
          return { valid: true, phoneNumber: pn.nationalNumber };
        }
      } catch (e1) { /* continue */ }

      try {
        var digits = phone.replace(/\D/g, '');
        var pn2 = lib.parsePhoneNumber(digits, selected.country);
        if (pn2 && typeof pn2.isValid === 'function' && pn2.isValid()) {
          return { valid: true, phoneNumber: pn2.nationalNumber };
        }
      } catch (e2) { /* continue */ }

      return {
        valid: false,
        message: themeStr('invalidPhoneFor', 'Please enter a valid __VAL__ phone number')
          .replace('__VAL__', placeName(countryLabel))
      };
    }

    // Fallback without lib
    var digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length === 0) {
      return { valid: false, message: themeStr('invalidPhone', 'Please enter a valid phone number') };
    }

    var dialDigits = (selected.code || '').replace(/\+/g, '');
    var phoneNumber = digitsOnly;
    if (dialDigits && phoneNumber.indexOf(dialDigits) === 0) {
      phoneNumber = phoneNumber.substring(dialDigits.length);
    }

    var pattern = PHONE_PATTERNS_FALLBACK[selected.country];
    if (pattern) {
      if (!pattern.test(phoneNumber)) {
        return {
          valid: false,
          message: themeStr('invalidPhoneFor', 'Please enter a valid __VAL__ phone number').replace('__VAL__', placeName(countryLabel))
        };
      }
    } else {
      if (phoneNumber.length < 7) {
        return { valid: false, message: themeStr('phoneTooShort', 'Phone number is too short') };
      }
      if (phoneNumber.length > 15) {
        return { valid: false, message: themeStr('phoneTooLong', 'Phone number is too long') };
      }
    }

    return { valid: true, phoneNumber: phoneNumber };
  }

  function showError(fieldId, message) {
    var field = document.getElementById(fieldId);
    var errorElement = document.getElementById(fieldId + '-error');

    if (field) {
      field.setAttribute('aria-invalid', 'true');
      field.classList.add('has-error');
    }

    if (errorElement) {
      errorElement.textContent = message || '';
      errorElement.innerHTML = message || '';
      errorElement.style.display = 'block';
      errorElement.style.visibility = 'visible';
      errorElement.style.opacity = '1';
      errorElement.classList.add('has-error');
      errorElement.setAttribute('data-error-shown', 'true');
    }
  }

  function clearError(fieldId) {
    var field = document.getElementById(fieldId);
    var errorElement = document.getElementById(fieldId + '-error');

    if (field) {
      field.setAttribute('aria-invalid', 'false');
      field.classList.remove('has-error');
    }

    if (errorElement) {
      errorElement.textContent = '';
      errorElement.innerHTML = '';
      errorElement.style.display = 'none';
      errorElement.style.visibility = 'hidden';
      errorElement.style.opacity = '0';
      errorElement.classList.remove('has-error');
      errorElement.removeAttribute('data-error-shown');
    }
  }

  function formatPhoneNumber(phone, selected) {
    if (!selected || !selected.country) return phone;

    var lib = getPhoneLib();
    if (lib && typeof lib.parsePhoneNumber === 'function') {
      try {
        var pn = lib.parsePhoneNumber(phone, selected.country);
        if (pn && typeof pn.isValid === 'function' && pn.isValid() && typeof pn.formatNational === 'function') {
          return pn.formatNational();
        }
      } catch (e) {
        try {
          var digits = phone.replace(/\D/g, '');
          var pn2 = lib.parsePhoneNumber(digits, selected.country);
          if (pn2 && pn2.isValid() && typeof pn2.formatNational === 'function') {
            return pn2.formatNational();
          }
        } catch (e2) { /* ignore */ }
      }
    }

    return phone.replace(/\D/g, '');
  }

  function handlePhoneInput(event) {
    var input = event.target;
    var selected = getSelectedCountryCode();

    if (!selected) return;

    var phoneValue = input.value;

    var digitsOnly = phoneValue.replace(/\D/g, '');
    if (phoneValue !== digitsOnly && event.type === 'input') {
      input.value = digitsOnly;
      phoneValue = digitsOnly;
    }

    if (digitsOnly.length > PHONE_NATIONAL_MAX) {
      input.value = digitsOnly.substring(0, PHONE_NATIONAL_MAX);
      phoneValue = input.value;
    }

    var countryCodeDigits = selected.code.replace(/\+/g, '');
    if (digitsOnly.indexOf(countryCodeDigits) === 0 && countryCodeDigits.length > 0) {
      var phoneWithoutCode = digitsOnly.substring(countryCodeDigits.length);
      if (phoneWithoutCode.length < digitsOnly.length) {
        input.value = phoneWithoutCode;
        phoneValue = phoneWithoutCode;
      }
    }

    if (event.type === 'blur') {
      if (!String(phoneValue).trim()) {
        clearError('contact-phone');
        return;
      }

      var formatted = formatPhoneNumber(phoneValue, selected);
      if (formatted !== phoneValue) {
        input.value = formatted;
        phoneValue = formatted;
      }

      var phoneValidation = validatePhone(phoneValue, selected);
      if (!phoneValidation.valid) {
        showError('contact-phone', phoneValidation.message);
      } else {
        clearError('contact-phone');
      }
    }

    if (event.type === 'input') {
      if (phoneValue.trim() === '') {
        clearError('contact-phone');
      }
    }
  }

  function handleCountryCodeChange() {
    var phoneInput = document.getElementById('contact-phone');
    if (!phoneInput) return;

    clearError('contact-phone');
    updateDialCodeDisplay();

    var selected = getSelectedCountryCode();
    if (!selected) return;

    phoneInput.setAttribute('maxlength', String(PHONE_NATIONAL_MAX));

    var row = findRowByIso(selected.country);
    if (row) {
      phoneInput.placeholder = themeStr('enterValidFor', 'Enter valid __VAL__ number')
        .replace('__VAL__', placeName(row.name));
    } else {
      phoneInput.placeholder = themeStr('enterPhone', 'Enter phone number');
    }
  }

  function getPhoneCountryRequiredMessage() {
    var form = document.getElementById('contact-details-form');
    if (form && form.getAttribute('data-msg-phone-country-required')) {
      return form.getAttribute('data-msg-phone-country-required');
    }
    return 'Select a country when entering a phone number';
  }

  function validateForm() {
    var email = document.getElementById('contact-email').value.trim();
    var phone = document.getElementById('contact-phone').value.trim();
    var selected = getSelectedCountryCode();

    var isValid = true;

    var emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      showError('contact-email', emailValidation.message);
      isValid = false;
    } else {
      clearError('contact-email');
    }

    if (!phone) {
      clearError('contact-phone');
    } else {
      if (!selected) {
        showError('contact-phone', getPhoneCountryRequiredMessage());
        isValid = false;
      } else {
        var phoneValidation = validatePhone(phone, selected);
        if (!phoneValidation.valid) {
          showError('contact-phone', phoneValidation.message);
          isValid = false;
        } else {
          clearError('contact-phone');
        }
      }
    }

    return isValid;
  }

  function getFormData() {
    var email = document.getElementById('contact-email').value.trim();
    var phone = document.getElementById('contact-phone').value.trim();
    var selected = getSelectedCountryCode();

    if (!phone) {
      return {
        email: email,
        phone: '',
        countryCode: '',
        country: ''
      };
    }

    if (!selected) {
      return {
        email: email,
        phone: phone,
        countryCode: '',
        country: ''
      };
    }

    var lib = getPhoneLib();
    if (lib && selected && typeof lib.parsePhoneNumber === 'function') {
      try {
        var pn = lib.parsePhoneNumber(phone, selected.country);
        if (pn && pn.isValid()) {
          return {
            email: email,
            phone: pn.format('E.164'),
            countryCode: '+' + String(pn.countryCallingCode),
            country: pn.country
          };
        }
      } catch (e) {
        try {
          var digits = phone.replace(/\D/g, '');
          var pn2 = lib.parsePhoneNumber(digits, selected.country);
          if (pn2 && pn2.isValid()) {
            return {
              email: email,
              phone: pn2.format('E.164'),
              countryCode: '+' + String(pn2.countryCallingCode),
              country: pn2.country
            };
          }
        } catch (e2) { /* fall through */ }
      }
    }

    var digitsOnly = phone.replace(/\D/g, '');
    var countryCodeDigits = selected.code.replace(/\+/g, '');
    var fullPhoneNumber = digitsOnly;
    if (fullPhoneNumber.indexOf(countryCodeDigits) !== 0) {
      fullPhoneNumber = selected.code + fullPhoneNumber;
    } else {
      fullPhoneNumber = '+' + fullPhoneNumber;
    }

    return {
      email: email,
      phone: fullPhoneNumber,
      countryCode: selected.code,
      country: selected.country
    };
  }

  function saveToLocalStorage(data) {
    try {
      localStorage.setItem('contact_details', JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save contact details to localStorage:', e);
    }
  }

  function selectDropdownByIso(dropdown, iso) {
    if (!dropdown || !iso) return false;
    for (var i = 0; i < dropdown.options.length; i++) {
      var opt = dropdown.options[i];
      var v = parseOptionValue(opt.value);
      if (v && v.country === iso) {
        dropdown.selectedIndex = i;
        return true;
      }
      if (opt.getAttribute('data-country') === iso) {
        dropdown.selectedIndex = i;
        return true;
      }
    }
    return false;
  }

  function loadFromLocalStorage() {
    try {
      var saved = localStorage.getItem('contact_details');
      if (!saved) return;

      var data = JSON.parse(saved);

      var emailInput = document.getElementById('contact-email');
      if (emailInput && data.email) {
        emailInput.value = data.email;
      }

      var phoneInput = document.getElementById('contact-phone');
      var countryCodeDropdown = document.getElementById('contact-country-code');

      if (!data.phone || !phoneInput || !countryCodeDropdown) return;

      var lib = getPhoneLib();

      if (lib && typeof lib.parsePhoneNumber === 'function') {
        try {
          var pn = lib.parsePhoneNumber(data.phone);
          if (pn && pn.country) {
            selectDropdownByIso(countryCodeDropdown, pn.country);
            handleCountryCodeChange();
            if (typeof pn.formatNational === 'function') {
              phoneInput.value = pn.formatNational();
            } else {
              phoneInput.value = String(pn.nationalNumber || '');
            }
            return;
          }
        } catch (e) { /* legacy path */ }
      }

      if (data.country && selectDropdownByIso(countryCodeDropdown, data.country)) {
        handleCountryCodeChange();
      }

      var savedDial = data.countryCode || '';
      if (!savedDial && data.phone.charAt(0) === '+') {
        var m = data.phone.match(/^\+(\d{1,3})/);
        if (m) savedDial = '+' + m[1];
      }

      var phoneNumber = data.phone.replace(/^\+/, '');
      var dialDigits = savedDial.replace(/\+/g, '');
      if (dialDigits && phoneNumber.indexOf(dialDigits) === 0) {
        phoneNumber = phoneNumber.substring(dialDigits.length);
      }

      phoneInput.value = phoneNumber;
    } catch (e) {
      console.warn('Failed to load contact details from localStorage:', e);
    }
  }

  function handleFormSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    var form = event.target;
    var submitButton = form.querySelector('.contact-details__submit');

    if (!validateForm()) {
      var firstError = form.querySelector('[aria-invalid="true"]');
      if (firstError) firstError.focus();
      return;
    }

    var formData = getFormData();
    saveToLocalStorage(formData);

    if (submitButton) {
      submitButton.classList.add('is-loading');
      submitButton.disabled = true;
    }

    var customEvent = new CustomEvent('contactDetailsCollected', {
      detail: formData,
      bubbles: true,
      cancelable: true
    });

    document.dispatchEvent(customEvent);
  }

  function closeContactDetailsDialog() {
    var dialog = document.getElementById('contact-details-dialog');
    var contactSheet = document.getElementById('contact-details-sheet');

    if (dialog && contactSheet) {
      try {
        if (contactSheet.popupClose && typeof contactSheet.popupClose === 'function') {
          contactSheet.popupClose();
        } else if (dialog.close && typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
          dialog.setAttribute('inert', '');
          dialog.setAttribute('aria-hidden', 'true');
        }
      } catch (error) {
        console.warn('[Contact Details] Error closing dialog:', error);
      }
    }
  }

  function handleMobileKeyboard() {
    if (window.innerWidth > 749) return;

    var form = document.getElementById('contact-details-form');
    var dialog = document.getElementById('contact-details-dialog');

    if (!form || !dialog) return;

    var inputs = form.querySelectorAll('input, select');
    inputs.forEach(function(input) {
      input.addEventListener('focus', function() {
        setTimeout(function() {
          var drawerInner = dialog.querySelector('.drawer__inner--bottom');
          if (drawerInner) {
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      });
    });
  }

  var isInitialized = false;
  var libWaitAttempts = 0;
  var LIB_WAIT_MAX = 100; // ~5s if libphonenumber script is slow or missing

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    var form = document.getElementById('contact-details-form');
    var countryCodeDropdown = document.getElementById('contact-country-code');
    var phoneInput = document.getElementById('contact-phone');
    var emailInput = document.getElementById('contact-email');

    if (!form) {
      if (!isInitialized) {
        setTimeout(init, 100);
      }
      return;
    }

    if (isInitialized) {
      return;
    }

    if (!getPhoneLib()) {
      libWaitAttempts += 1;
      if (libWaitAttempts < LIB_WAIT_MAX) {
        setTimeout(init, 50);
        return;
      }
      console.warn(
        '[Contact Details] libphonenumber-min.js not found. Using a short country list and basic validation.'
      );
    }

    initCountryCodeDropdown();

    if (phoneInput) {
      phoneInput.setAttribute('maxlength', String(PHONE_NATIONAL_MAX));
    }

    handleCountryCodeChange();
    loadFromLocalStorage();

    form.addEventListener('submit', handleFormSubmit);

    if (countryCodeDropdown) {
      countryCodeDropdown.addEventListener('change', handleCountryCodeChange);
    }

    if (phoneInput) {
      phoneInput.addEventListener('input', handlePhoneInput);
      phoneInput.addEventListener('blur', handlePhoneInput);
    }

    if (emailInput) {
      emailInput.addEventListener('input', function() {
        if (emailInput.value.trim() === '') {
          clearError('contact-email');
        }
      });

      emailInput.addEventListener('blur', function() {
        var validation = validateEmail(emailInput.value);
        if (!validation.valid) {
          showError('contact-email', validation.message);
        } else {
          clearError('contact-email');
        }
      });
    }

    handleMobileKeyboard();

    isInitialized = true;

    var dialog = document.getElementById('contact-details-dialog');
    if (dialog) {
      dialog.addEventListener('close', function() {
        isInitialized = false;
      });
    }
  }

  init();

  window.ContactDetailsCollector = {
    getFormData: getFormData,
    validateForm: validateForm,
    getSelectedCountryCode: getSelectedCountryCode,
    closeDialog: closeContactDetailsDialog
  };
})();
